import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { isNil, mapValues, orderBy, uniqBy } from 'lodash'
import ReferencialAction from '../../action/ReferencialAction'
import { nbPerPageLabel, SANDRE } from '../../constants/ReferencialConstants'
import { push } from '@lagunovsky/redux-react-router'
import { setLocalStorageJson } from '../../../utils/FormUtils'
import Select from '../../../components/forms/Select'
import { getDate } from '../../../utils/DateUtil'
import { getSandreLabel, searchAllCharacters } from '../../../utils/StringUtil'
import { getStatusInformation } from '../../util/StatusUtils'
import logoSandre from 'assets/pictures/logo_sandre.png'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import Input from 'components/forms/Input'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import { hasValue } from 'utils/NumberUtil'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'

const headers = ['displayField', 'code', 'name', 'mnemonique', 'displayStatus', 'reference']

const SandreCodesApp = () => {
    const {
        sandreCodes,
        referencialSandreCodes,
        referencialStatus,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        referencialSandreCodes: store.ReferencialReducer.referencialSandreCodes,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const [filter, setFilter] = useLocalStorage('SANDRE_FILTER', {})
    const [filterTmp, setFilterTmp] = useLocalStorage('SANDRE_FILTER', {})

    const dispatch = useDispatch()

    useEffect(() => {
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: '/referencial',
    }, {
        title: i18n.sandreNomenclature,
        href: '/referencial/sandreCodes',
    }], [])

    const refSandre = useMemo(() => referencialSandreCodes.find(rsf => rsf.field === filter.selectedField) || {}, [filter.selectedField, referencialSandreCodes])

    const filteredList = useMemo(() => {
        return filter.selectedField ? sandreCodes.filter(sandreCode => sandreCode.field === filter.selectedField) : sandreCodes
    }, [filter.selectedField, sandreCodes])

    const refList = useMemo(() => {
        return filteredList.filter(sandreCode => referencialSandreCodes.some(rsc => rsc.code && rsc.field === sandreCode.field))
    }, [filteredList, referencialSandreCodes])

    const sandreCodesByStatus = useMemo(() => hasValue(filter.status) ? refList.filter(s => s.status === filter.status) : refList, [filter.status, refList])

    const dataSandreCodes = useMemo(() => orderBy(sandreCodesByStatus, ['field', 'code']).map(sandreCode => ({
        ...sandreCode,
        displayField: `${sandreCode.field} \[${referencialSandreCodes.find(rsf => rsf.field === sandreCode.field)?.code}\]`,
        sandreCode: referencialSandreCodes.find(rsf => rsf.field === sandreCode.field).code,
        code: `${sandreCode.code}`,
        creationDate: getDate(sandreCode.date),
        updateDate: getDate(sandreCode.updateDate),
        displayStatus: sandreCode.status === 3 ? getStatusInformation('3') : getSandreLabel(sandreCodes, SANDRE.DISPOSITIFS_ETATS, sandreCode.status),
        status: getSandreLabel(sandreCodes, SANDRE.DISPOSITIFS_ETATS, sandreCode.status),
        headers: ['field', 'sandreCode', 'code', 'name', 'mnemonique', 'status', 'reference', 'comment', 'creationDate', 'updateDate', 'author'],
    })), [sandreCodesByStatus, referencialSandreCodes, sandreCodes])

    const data = useMemo(() => {
        return dataSandreCodes
            .filter((obj) => searchAllCharacters(headers.map((key) => obj[key]).join())
                .includes(searchAllCharacters(filter.searchValue || '')))
    }, [dataSandreCodes, filter])

    const colorData = useMemo(() => data.map(u => ({ ...mapValues(u, o => ({ value: o })), fieldLink: u.field, id: u.code })), [data])

    useActions(() => {
        const actions = {
            new: () => dispatch(push('/referencial/sandreCode/new/-1/dashboard')),
            referencialActions: {
                actions: {
                    export: {
                        data,
                        titleFile: i18n.sandreNomenclature,
                    },
                },
            },
        }
        return isNil(refSandre?.code) ? actions : {
            ...actions,
            links: [{
                href: `http://www.sandre.eaufrance.fr/urn.php?urn=urn:sandre:donnees:NSA:FRA:code:${refSandre.code}:::referentiel:${refSandre.referentialNumber}:html`,
                img: logoSandre,
                label: i18n.sandre.toUpperCase(),
            }],
        }
    }, [filter, refSandre])

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }

        if (!referencialSandreCodes.length) {
            dispatch(ReferencialAction.fetchReferencialSandreCodes())
        }
    })

    const onFilterChange = (newFilter) => {
        setLocalStorageJson('SANDRE_FILTER', newFilter)
        setFilterTmp(newFilter)
    }

    const getDisplay = refFilter => {
        return refFilter && (refFilter.updateDate || refFilter.updateUser) && `${i18n.lastUpdate}
            ${refFilter.updateDate && ` ${i18n.atDate} ${getDate(refFilter.updateDate)}` || ''}
            ${refFilter.updateUser && ` ${i18n.udpdateBy} ${refFilter.updateUser}` || ''}`
    }

    return (
        <Stack margin={1} spacing={1}>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={4}>
                            <Select
                                label={i18n.nomenclature}
                                value={filterTmp.selectedField}
                                options={orderBy(uniqBy(referencialSandreCodes.filter(rsf => rsf.code), 'field'), 'field')}
                                onChange={v => onFilterChange({ ...filterTmp, selectedField: v })}
                                nullLabel='&nbsp;'
                                keyValue='field'
                                keyLabel='field'
                            />
                        </Grid>
                        <Grid size={3}>
                            <Input
                                title={i18n.search}
                                value={filterTmp.searchValue}
                                onChange={v => setFilterTmp({ ...filterTmp, searchValue: v })}
                                onEnterKeyPress={() => setFilter({ ...filterTmp })}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilterTmp({ ...filterTmp, status: v })}
                                value={filterTmp.status}
                                nullLabel='&nbsp;'
                            />
                        </Grid>
                        <Grid size={2} marginTop={2}>
                            <Button className='right' onClick={() => setFilter({ ...filterTmp })} variant='contained' color='primary'>
                                {i18n.search}
                            </Button>
                        </Grid>
                        {
                            !refSandre && (
                                <Grid size={12}>
                                    {getDisplay(refSandre)}
                                </Grid>
                            )
                        }
                    </Grid>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.sandreNomenclature}
                headersLabel={{ displayField: i18n.field, displayStatus: i18n.status }}
                color='bannerColor'
                rowsPerPageOptions={nbPerPageLabel}
                rows={colorData}
                headers={headers}
                onClickRow={element => dispatch(push(`/referencial/sandreCode/${element.fieldLink}/${element.id}/dashboard`))}
            />
        </Stack>
    )
}

export default SandreCodesApp